import {
  createVoiceAgentStore,
  createPublicSession,
  sendVisitorBeacon,
} from "@moss-js/founding-agent/core";
import type { StoreApi } from "zustand/vanilla";
import type { VoiceAgentStore } from "@moss-js/founding-agent/core";
import { resolveConfig, fetchPublicConfig, inputFromDataset } from "./config.js";
import { ensureRegistered } from "./element.js";
import type { MossFoundingAgentElement } from "./element.js";
import { createTransport } from "./transport.js";
import { createBubble } from "./ui/bubble.js";
import { createChatState } from "./ui/chat-state.js";
import { createHandoffToast } from "./ui/handoff.js";
import { createMicErrorOverlay } from "./ui/mic-error.js";
import { createBookingModal } from "./ui/booking.js";
import { createIntakeModal } from "./ui/intake.js";
import { createTranscript } from "./ui/transcript.js";
import { bindKeyboard } from "./keyboard.js";
import type { WidgetInput } from "./config.js";
import type { TranscriptHandle } from "./ui/transcript.js";

export type { WidgetInput };

type EventName = "connect" | "disconnect" | "error" | "transcript" | "bookingComplete" | "handoff";
type EventCallback = (...args: unknown[]) => void;

export interface WidgetInstance {
  id: string;
  open(): void;
  end(): void;
  setMuted(muted: boolean): void;
  destroy(): void;
  on(event: EventName, cb: EventCallback): void;
  off(event: EventName, cb: EventCallback): void;
}

// Registry: keyed by the host HTMLElement (body-appended for fixed, target el for inline)
const registry = new Map<HTMLElement, WidgetInstance>();
// Secondary registry keyed by instance id, so destroy(id) can find an instance.
const byId = new Map<string, WidgetInstance>();
let idCounter = 0;

// Generate an auto id that isn't already registered (collision-safe).
function nextAutoId(): string {
  let candidate: string;
  do {
    candidate = `moss-fa-${++idCounter}`;
  } while (byId.has(candidate));
  return candidate;
}

// Managed fixed-position host element (single instance across all fixed-position inits)
let fixedHost: HTMLElement | null = null;

function getOrCreateFixedHost(): HTMLElement {
  if (!fixedHost || !document.body.contains(fixedHost)) {
    fixedHost = document.createElement("div");
    fixedHost.style.cssText = "position:fixed;top:0;left:0;width:0;height:0;overflow:visible;z-index:2147483647;";
    document.body.appendChild(fixedHost);
  }
  return fixedHost;
}

function fire(listeners: Map<EventName, Set<EventCallback>>, event: EventName, ...args: unknown[]): void {
  listeners.get(event)?.forEach(cb => {
    try { cb(...args); } catch { /* callbacks must not crash the widget */ }
  });
}

// Validate a selector option once; an invalid string throws from querySelectorAll.
function isValidSelector(selector: string, option: string): boolean {
  try {
    document.querySelectorAll(selector);
    return true;
  } catch {
    console.warn(`MossFoundingAgent: invalid ${option} "${selector}"; ignoring`);
    return false;
  }
}

// Keep `io` observing exactly the elements matching `selector`. MutationObserver
// picks up matches added/removed after init; `onSync` gets both lists after each
// sync. Returns a cleanup function.
function observeMatches(
  selector: string,
  io: IntersectionObserver,
  onSync: (added: Element[], removed: Element[]) => void,
): () => void {
  let disposed = false;
  const observed = new Set<Element>();

  const sync = (): void => {
    const matches = new Set<Element>(document.querySelectorAll(selector));
    const added = [...matches].filter((elm) => !observed.has(elm));
    const removed = [...observed].filter((elm) => !matches.has(elm));
    for (const elm of added) { observed.add(elm); io.observe(elm); }
    for (const elm of removed) { observed.delete(elm); io.unobserve(elm); }
    onSync(added, removed);
  };

  sync();

  // Coalesce bursts of DOM mutations (common on SPA pages) into one sync.
  let scheduled = false;
  let mo: MutationObserver | null = null;
  if (typeof MutationObserver !== "undefined" && document.body) {
    mo = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      queueMicrotask(() => {
        scheduled = false;
        if (!disposed) sync();
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  return () => {
    disposed = true;
    mo?.disconnect();
  };
}

// Hide `hostEl` while any element matching `selector` is in the viewport (e.g.
// hide the bubble over the footer / a contact section). No-ops where
// IntersectionObserver is unavailable (e.g. jsdom). Returns a cleanup function.
// `display:none` targets never intersect, so they never trigger a hide.
function setupHideOnSelector(hostEl: HTMLElement, selector: string, store: StoreApi<VoiceAgentStore>): () => void {
  if (typeof IntersectionObserver === "undefined" || typeof document === "undefined") {
    return () => {};
  }
  if (!isValidSelector(selector, "hideOnSelector")) return () => {};

  // Set on cleanup so queued observer callbacks can't touch a torn-down widget.
  let disposed = false;
  const intersecting = new Set<Element>();

  const isActive = (): boolean => {
    const s = store.getState().connectionState;
    return s === "connecting" || s === "connected";
  };

  const apply = (): void => {
    hostEl.style.visibility = (intersecting.size > 0 && !isActive()) ? "hidden" : "";
  };

  const io = new IntersectionObserver((entries) => {
    if (disposed) return;
    for (const entry of entries) {
      if (entry.isIntersecting) intersecting.add(entry.target);
      else intersecting.delete(entry.target);
    }
    apply();
  });

  const stopMatching = observeMatches(selector, io, (_added, removed) => {
    for (const elm of removed) intersecting.delete(elm);
    apply();
  });

  const unsubConn = store.subscribe((s, p) => {
    if (s.connectionState !== p.connectionState) apply();
  });

  return () => {
    disposed = true;
    unsubConn();
    stopMatching();
    io.disconnect();
    hostEl.style.visibility = "";
  };
}

// Lift the fixed bubble above any visible element matching `selector` that sits
// level with or below the resting pill (a cookie banner or another fixed bar)
// and drop it back once none does. The lift goes to --_moss-avoid-lift on the
// host, which the stylesheet adds to the bottom offset. Overlap is measured
// against the unlifted position so an applied lift never feeds back into
// itself. Meant for fixed elements: one that scrolls with the page, like a
// footer taller than the viewport, would push the bubble to the top, so
// hideOnSelector suits those.
function setupAvoidSelector(hostEl: HTMLElement, column: HTMLElement, selector: string): () => void {
  if (typeof IntersectionObserver === "undefined" || typeof document === "undefined") {
    return () => {};
  }
  if (!isValidSelector(selector, "avoidSelector")) return () => {};

  let disposed = false;
  let lift = 0;
  const intersecting = new Set<Element>();

  const measure = (): void => {
    if (disposed) return;
    let next = 0;
    if (intersecting.size > 0) {
      const col = column.getBoundingClientRect();
      const restingBottom = col.bottom + lift;
      const pillTop = restingBottom - (column.querySelector<HTMLElement>("[data-moss-bubble]")?.offsetHeight || 56);
      for (const elm of intersecting) {
        const style = getComputedStyle(elm);
        if (style.visibility === "hidden" || style.opacity === "0") continue;
        const r = elm.getBoundingClientRect();
        // Anything level with or below the pill keeps the bubble its usual gap
        // above the element's top edge, so it rises smoothly with a bar sliding in.
        if (r.left < col.right && r.right > col.left && r.bottom > pillTop) {
          next = Math.max(next, window.innerHeight - r.top);
        }
      }
      // Never push the widget (pill, or the open chat panel above it) off the top.
      next = Math.round(Math.min(next, Math.max(0, col.top + lift - 8)));
    }
    if (next === lift) return;
    lift = next;
    if (lift > 0) hostEl.style.setProperty("--_moss-avoid-lift", `${lift}px`);
    else hostEl.style.removeProperty("--_moss-avoid-lift");
  };

  // One measure per frame, however many triggers fire.
  let pending = false;
  let frameId = 0;
  const schedule = (): void => {
    if (pending || disposed) return;
    pending = true;
    frameId = requestAnimationFrame(() => {
      pending = false;
      measure();
    });
  };

  // Several thresholds so a banner sliding in re-measures as it moves.
  const io = new IntersectionObserver((entries) => {
    if (disposed) return;
    for (const entry of entries) {
      if (entry.isIntersecting) intersecting.add(entry.target);
      else intersecting.delete(entry.target);
    }
    schedule();
  }, { threshold: [0, 0.25, 0.5, 0.75, 1] });

  // A match changing height, or the column widening (chat panel), moves the overlap.
  const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
  ro?.observe(column);

  const stopMatching = observeMatches(selector, io, (added, removed) => {
    for (const elm of added) ro?.observe(elm);
    for (const elm of removed) {
      ro?.unobserve(elm);
      intersecting.delete(elm);
    }
    schedule();
  });

  // Neither observer sees a banner fade (opacity/visibility), and a slide can
  // finish between IntersectionObserver thresholds, so re-measure when a
  // transition or animation ends.
  const onSettle = (): void => {
    if (intersecting.size > 0) schedule();
  };
  window.addEventListener("resize", schedule);
  document.addEventListener("transitionend", onSettle, true);
  document.addEventListener("animationend", onSettle, true);

  return () => {
    disposed = true;
    cancelAnimationFrame(frameId);
    stopMatching();
    io.disconnect();
    ro?.disconnect();
    window.removeEventListener("resize", schedule);
    document.removeEventListener("transitionend", onSettle, true);
    document.removeEventListener("animationend", onSettle, true);
    hostEl.style.removeProperty("--_moss-avoid-lift");
  };
}

export function init(input: WidgetInput): WidgetInstance {
  const config = resolveConfig(input);
  let id = config.id ?? nextAutoId();

  // Determine the host element. Inline mode requires a target; without one we
  // fall back to the fixed host (and must stamp the element "fixed", not
  // "inline", or the CSS would make the bubble position:static inside a
  // zero-size fixed host).
  let hostEl: HTMLElement;
  let mountedInline = false;
  if (config.position === "inline" && config.target) {
    const found = document.querySelector<HTMLElement>(config.target);
    if (!found) throw new Error(`MossFoundingAgent: target element not found: "${config.target}"`);
    hostEl = found;
    mountedInline = true;
  } else {
    if (config.position === "inline") {
      console.warn('MossFoundingAgent: position "inline" requires a target; falling back to fixed positioning');
    }
    hostEl = getOrCreateFixedHost();
  }

  // Idempotent: return existing instance for same host
  const existing = registry.get(hostEl);
  if (existing) {
    console.warn("MossFoundingAgent: already initialized on this target, returning existing instance");
    return existing;
  }

  // A duplicate explicit id on a different host would clobber the byId registry
  // (making destroy(id) unreachable for the first instance). Regenerate instead.
  if (config.id && byId.has(config.id)) {
    console.warn(`MossFoundingAgent: id "${config.id}" is already in use; generating a unique id instead`);
    id = nextAutoId();
  }

  // Ensure the custom element is registered (idempotent per jsdom environment)
  ensureRegistered();

  // Create the custom element
  const el = document.createElement("moss-founding-agent") as MossFoundingAgentElement;
  el.dataset.position = mountedInline ? "inline" : "fixed";
  // Side and offsets only place the fixed bubble; inline flows with its target.
  if (!mountedInline) {
    el.dataset.side = config.side;
    if (config.offsetX !== undefined) el.style.setProperty("--moss-offset-x", `${config.offsetX}px`);
    if (config.offsetY !== undefined) el.style.setProperty("--moss-offset-y", `${config.offsetY}px`);
  }
  hostEl.appendChild(el);

  // Per-instance store
  const store = createVoiceAgentStore({
    publishableKey: config.publishableKey,
    serviceUrl: config.serviceUrl,
  });

  // Transport starts non-interactive (fail-closed): the widget only drives the
  // visitor's browser (navigate / scroll / page-context) once /config confirms a
  // non-static agent_mode. If config never loads, that power is never granted.
  const transport = createTransport(store, {
    effects: {
      interactive: false,
      navigate: (path) => {
        try {
          if (config.onNavigate) { config.onNavigate(path); return; }
          // The default fallback drives window.location with agent-controlled
          // input, so restrict it to same-origin relative paths (the
          // navigate_to_page contract: an absolute path like "/pricing"). This
          // rejects "javascript:"/"data:" schemes, absolute URLs, and
          // protocol-relative "//host" and "/\\host" values (the URL parser
          // treats "\\" like "/" for special schemes). SPA hosts pass onNavigate.
          if (
            typeof window === "undefined" ||
            typeof path !== "string" ||
            !path.startsWith("/") ||
            /^\/[/\\]/.test(path)
          ) return;
          const resolved = new URL(path, window.location.origin);
          if (resolved.origin !== window.location.origin) return;
          window.location.assign(resolved.pathname + resolved.search + resolved.hash);
        } catch { /* ignored */ }
      },
      scrollToSection: (selector) => {
        try { document.querySelector(selector)?.scrollIntoView({ behavior: "smooth" }); } catch { /* ignored */ }
      },
      publishPageContext: () => {
        try { transport.publishPageContext(); } catch { /* ignored */ }
      },
    },
  });

  // Transcript panel (mounted into the overlay area shown while connected)
  let transcriptHandle: TranscriptHandle | null = null;

  // Event listeners registry
  const listeners = new Map<EventName, Set<EventCallback>>();

  // Keyboard unbind handle (set when active, cleared on end)
  let unbindKeyboard: (() => void) | null = null;

  // Guard against double-destroy
  let destroyed = false;

  // Monotonically increasing counter to invalidate in-flight open() calls when
  // end() or destroy() is called before the awaits resolve.
  let openGeneration = 0;

  // hideOnSelector / avoidSelector observer cleanups (set below if configured).
  let cleanupHideOnSelector: (() => void) | null = null;
  let cleanupAvoidSelector: (() => void) | null = null;

  // Subscribe to store for lifecycle callbacks
  const storeUnsub = store.subscribe((state, prev) => {
    if (state.connectionState !== prev.connectionState) {
      if (state.connectionState === "connected") {
        try { config.onConnect?.(); } catch { /* ignored */ }
        fire(listeners, "connect");
      } else if (prev.connectionState === "connected" && state.connectionState === "idle") {
        try { config.onDisconnect?.(); } catch { /* ignored */ }
        fire(listeners, "disconnect", { reason: "ended" });
        unbindKeyboard?.();
        unbindKeyboard = null;
        store.getState().reset();
      } else if (prev.connectionState === "connected" && state.connectionState === "error") {
        try { config.onDisconnect?.(); } catch { /* ignored */ }
        fire(listeners, "disconnect", { reason: "error" });
        unbindKeyboard?.();
        unbindKeyboard = null;
        store.getState().reset();
      }
    }
    if (state.transcript !== prev.transcript && state.transcript.length > prev.transcript.length) {
      const newest = state.transcript[state.transcript.length - 1];
      if (newest) {
        try { config.onTranscript?.(newest.text); } catch { /* ignored */ }
        fire(listeners, "transcript", newest.text);
      }
    }
    if (state.bookingResult !== prev.bookingResult && state.bookingResult !== null && prev.bookingResult === null) {
      try { config.onBookingComplete?.(); } catch { /* ignored */ }
      fire(listeners, "bookingComplete");
    }
    if (state.handoffStatus !== prev.handoffStatus && state.handoffStatus === "joined") {
      try { config.onHandoff?.(); } catch { /* ignored */ }
      fire(listeners, "handoff");
    }
  });

  // Overlay layer (shadow DOM .moss-overlay or fallback to element)
  const overlayRoot = el.shadowRoot
    ? (el.shadowRoot.querySelector(".moss-overlay") as HTMLElement ?? el)
    : el;
  // Bubble column: pill, attribution and chat panel share it (and its theme).
  const bubbleRoot = el.shadowRoot
    ? (el.shadowRoot.querySelector(".moss-bubble") as HTMLElement ?? el)
    : el;
  const chat = createChatState();
  // Mounts the transcript panel on first call; later calls only swap the
  // suggested-prompt chips. open()'s 3s config timeout can mount it with none
  // before a slow /config arrives with the configured prompts.
  const mountTranscript = (suggestedPrompts: string[]): void => {
    if (transcriptHandle) transcriptHandle.setPrompts(suggestedPrompts);
    else transcriptHandle = createTranscript(bubbleRoot, store, { suggestedPrompts, chat });
  };

  // Overlays
  const micError = createMicErrorOverlay(overlayRoot, store);
  const handoff = createHandoffToast(overlayRoot, store);
  const booking = createBookingModal(overlayRoot, store, {
    serviceUrl: config.serviceUrl,
    publishableKey: config.publishableKey,
    publisher: transport.bookingPublisher,
  });
  const intake = createIntakeModal(overlayRoot, store, {
    serviceUrl: config.serviceUrl,
    publishableKey: config.publishableKey,
    publisher: transport.intakePublisher,
  });

  // Bubble
  const bubble = createBubble(bubbleRoot, store, {
    color: config.color,
    themeMode: config.themeMode,
    appearance: config.appearance,
    label: config.label,
    chat,
    publishableKey: config.publishableKey,
    open: async () => instanceExtended.open(),
    end: () => instanceExtended.end(),
    toggleMute: () => {
      const s = store.getState();
      transport.setMuted(!s.isMicMuted);
    },
  });

  // Fire config fetch (non-blocking). Kept as a handle so open() can settle the
  // interactive/static policy before connecting.
  const configReady = fetchPublicConfig(config.serviceUrl, config.publishableKey).then((publicConfig) => {
    if (destroyed) return;
    (el as MossFoundingAgentElement & { _publicConfig?: unknown })._publicConfig = publicConfig;

    // Grant interactivity only for a confirmed non-static agent (fail-closed).
    transport.setInteractive(publicConfig.agent_mode !== "static");

    // Portal-configured look. Both are no-ops when the corresponding init
    // option was passed explicitly (explicit wins over fetched).
    bubble.setAppearance(publicConfig.appearance);
    bubble.setTheme(publicConfig.appearance?.widget_theme);
    bubble.setTeaser(publicConfig.teaser ?? null);

    // Feed the portal-configured intake form into the store. reset() preserves
    // intakeConfig, so setting it once here (at fetch time) is enough. title's
    store.getState().setIntakeConfig(publicConfig.intake_form ?? null);

    // Mount transcript panel with suggested prompts from config
    mountTranscript(publicConfig.suggested_prompts ?? []);
  }).catch((err: Error) => {
    if (destroyed) return;
    // Config fetch failed; leave the widget non-interactive (fail-closed) and
    // mount the transcript without suggested-prompt chips.
    mountTranscript([]);
    try { config.onError?.(err); } catch { /* ignored */ }
  });

  // Internal seams used only in tests (not on the WidgetInstance public type):
  // __emit fires listeners; __store/__transport expose the per-instance store
  // and transport so tests can drive and observe production wiring.
  const instanceExtended: WidgetInstance & {
    __emit: (event: EventName, ...args: unknown[]) => void;
    __store: unknown;
    __transport: unknown;
  } = {
    id,
    __emit(event: EventName, ...args: unknown[]) {
      fire(listeners, event, ...args);
    },
    __store: store,
    __transport: transport,
    async open() {
      const { connectionState } = store.getState();
      if (connectionState === "connecting" || connectionState === "connected") return;

      const gen = ++openGeneration;

      store.getState().setConnectionState("connecting");

      // Bind now so Escape can abandon a slow connect (session fetch, config
      // race, LiveKit connect). end() increments openGeneration, superseding
      // the in-flight open().
      unbindKeyboard?.();
      unbindKeyboard = bindKeyboard(
        () => {
          const s = store.getState();
          const active = s.connectionState === "connecting" || s.connectionState === "connected";
          return { active, connected: s.connectionState === "connected" };
        },
        {
          end: () => instanceExtended.end(),
          toggleMute: () => {
            const s = store.getState();
            transport.setMuted(!s.isMicMuted);
          },
        },
      );

      // Session fetch: partial clear on failure (preserve booking/transcript/handoff)
      let sess: Awaited<ReturnType<typeof createPublicSession>>;
      try {
        sess = await createPublicSession({
          publishableKey: config.publishableKey,
          serviceUrl: config.serviceUrl,
        });
      } catch (err) {
        // Cancelled (end()/destroy()) while the session fetch was pending: do
        // not flip to error or fire onError for a call the user abandoned.
        if (destroyed || gen !== openGeneration) return;
        store.setState({
          connectionState: "error",
          token: null,
          wsUrl: null,
        });
        try { config.onError?.(err instanceof Error ? err : new Error(String(err))); } catch { /* ignored */ }
        fire(listeners, "error", err);
        return;
      }

      // Bail if the widget was destroyed or end() was called while session fetch was in flight.
      if (destroyed || gen !== openGeneration) return;

      // Session obtained; store token/wsUrl/bookingNonce
      store.setState({
        token: sess.token,
        wsUrl: sess.serverUrl,
        bookingNonce: sess.bookingNonce,
      });

      // Settle the interactive/static policy from /config before connecting, so
      // the agent can't send navigate/scroll/page-context effects while the
      // widget is still non-interactive. Bounded so a slow or dead config
      // endpoint can't block the call (we stay fail-closed if it never arrives).
      await Promise.race([
        configReady,
        new Promise<void>((resolve) => setTimeout(resolve, 3000)),
      ]);
      if (destroyed || gen !== openGeneration) return;

      // Fallback: if /config never settled (hung or very slow), ensure the
      // transcript panel exists before connecting so the call always has one.
      // Mount only: a config that already landed keeps its prompts.
      if (!transcriptHandle) mountTranscript([]);

      // Transport connect: full reset on failure (clears stale bookingNonce too)
      try {
        await transport.connect(sess.token, sess.serverUrl);
      } catch (err) {
        // Cancelled while connecting: end()/destroy() already tore down; don't
        // reset again or fire error for an abandoned call.
        if (destroyed || gen !== openGeneration) return;
        store.getState().reset();
        try { config.onError?.(err instanceof Error ? err : new Error(String(err))); } catch { /* ignored */ }
        fire(listeners, "error", err);
        return;
      }
      if (destroyed || gen !== openGeneration) {
        transport.disconnect();
        return;
      }

      // Connect succeeded; fire beacon (keyboard was bound at open start)
      void sendVisitorBeacon({
        serviceUrl: config.serviceUrl,
        publishableKey: config.publishableKey,
        bookingNonce: sess.bookingNonce,
      });
    },

    end() {
      openGeneration++;
      unbindKeyboard?.();
      unbindKeyboard = null;
      transport.disconnect();
      store.getState().reset();
      // disconnect event fires via store subscription (connected -> idle transition).
    },

    setMuted(muted: boolean) {
      transport.setMuted(muted);
    },

    destroy() {
      if (destroyed) return;
      destroyed = true;
      openGeneration++;
      cleanupHideOnSelector?.();
      cleanupHideOnSelector = null;
      cleanupAvoidSelector?.();
      cleanupAvoidSelector = null;
      storeUnsub();
      unbindKeyboard?.();
      unbindKeyboard = null;
      bubble.destroy();
      micError.destroy();
      handoff.destroy();
      booking.destroy();
      intake.destroy();
      transcriptHandle?.destroy();
      transcriptHandle = null;
      transport.destroy();
      el.remove();
      // mountedInline records the actual mount: position "inline" without a
      // target falls back to the fixed host, which must be removed too.
      if (!mountedInline && hostEl === fixedHost) {
        hostEl.remove();
        fixedHost = null;
      }
      registry.delete(hostEl);
      byId.delete(id);
      listeners.clear();
    },

    on(event: EventName, cb: EventCallback) {
      if (!listeners.has(event)) listeners.set(event, new Set());
      listeners.get(event)!.add(cb);
    },

    off(event: EventName, cb: EventCallback) {
      listeners.get(event)?.delete(cb);
    },
  };

  registry.set(hostEl, instanceExtended);
  byId.set(id, instanceExtended);

  // Optionally hide the widget while an element matching hideOnSelector is in
  // the viewport (e.g. hide the bubble over the footer / a contact section).
  if (config.hideOnSelector) {
    cleanupHideOnSelector = setupHideOnSelector(el, config.hideOnSelector, store);
  }

  // Optionally lift the fixed bubble above elements that would cover it (e.g.
  // a cookie banner along the bottom of the page).
  if (config.avoidSelector && !mountedInline) {
    cleanupAvoidSelector = setupAvoidSelector(el, bubbleRoot, config.avoidSelector);
  }

  return instanceExtended;
}

export function destroy(id?: string): void {
  if (id === undefined) return;
  byId.get(id)?.destroy();
}

export function destroyAll(): void {
  for (const [hostEl, inst] of [...registry.entries()]) {
    inst.destroy();
    registry.delete(hostEl);
  }
  if (fixedHost) {
    fixedHost.remove();
    fixedHost = null;
  }
}

// Global API object
const api = { init, destroy, destroyAll };

declare global {
  interface Window {
    MossFoundingAgent?: typeof api & { q?: WidgetInput[] };
  }
}

// Drain any calls queued before this script loaded, then run data-* auto-init.
// Kept in a function so it can be deferred to DOMContentLoaded when the script
// is placed in <head> (body not yet available at module eval time).
const existing = (globalThis as unknown as Window).MossFoundingAgent;
(globalThis as unknown as Window).MossFoundingAgent = api;

function drainAndAutoInit(): void {
  const q = existing?.q ?? [];
  // Queued inputs come from untrusted script order; cast to RawWidgetInput so
  // resolveConfig's runtime checks apply even if the caller passed bad types.
  for (const input of q) { try { api.init(input as unknown as WidgetInput); } catch { /* skip bad queued input */ } }
  if (typeof document !== "undefined") {
    const autoInitEls = document.querySelectorAll<HTMLElement>("[data-moss-pk]");
    for (const el of autoInitEls) {
      if (el.dataset.mossPk) {
        try {
          // Data-attribute values are always strings; cast at this internal
          // boundary so resolveConfig's runtime checks still validate them.
          api.init(inputFromDataset(el.dataset) as unknown as WidgetInput);
        } catch {
          // Silently skip invalid auto-init attributes
        }
      }
    }
  }
}

if (typeof document !== "undefined") {
  if (document.readyState === "loading" || !document.body) {
    document.addEventListener("DOMContentLoaded", drainAndAutoInit, { once: true });
  } else {
    drainAndAutoInit();
  }
}
